import Phaser from 'phaser';
import type { CatDefinition, Lane } from '@/types';
import {
  CAT_CATCH_HALF_WIDTH,
  CAT_DISPLAY_SIZE,
  CAT_MOUTH_OFFSET_Y,
  CAT_MOVE_SMOOTHING,
  LANE_WIDTH,
} from '@/game/config/GameConfig';
import { smoothApproach } from '@/utils/rhythmMath';

export type CatPose = 'idle' | 'anticipate' | 'eat';

/** Texture key for a given cat + pose. Shared with PreloadScene so the loader and the
 * renderer always agree on naming. */
export function catTextureKey(catId: string, pose: CatPose): string {
  return `cat-${catId}-${pose}`;
}

const MISS_TINT = 0x93a0c9;

/**
 * A cat is a single AI-illustrated sprite (see PreloadScene for how the
 * `idle`/`anticipate`/`eat` textures are loaded) that swaps texture for its
 * anticipate/eat poses and otherwise conveys state through tweens --
 * squash/stretch, tint, rotation -- rather than independently-animated sub-parts.
 * This is a deliberate simplification versus the original placeholder (which
 * composited body/ears/tail/mouth as separate shapes): a full illustration reads
 * much better, at the cost of losing per-part animation like an independent tail wag.
 */
export class Cat extends Phaser.GameObjects.Container {
  private lane: Lane;
  private currentNormX = 0.5;
  private laneOffsetPx: number;
  private catId: string;

  private sprite: Phaser.GameObjects.Image;
  private sparkle?: Phaser.GameObjects.Star;

  private idleTween?: Phaser.Tweens.Tween;
  private revertPoseTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene, lane: Lane, def: CatDefinition, restY: number) {
    const laneOffsetPx = lane === 'left' ? 0 : LANE_WIDTH;
    super(scene, laneOffsetPx + LANE_WIDTH * 0.5, restY);
    this.lane = lane;
    this.laneOffsetPx = laneOffsetPx;
    this.catId = def.id;
    scene.add.existing(this);

    this.sprite = scene.add.image(0, 0, catTextureKey(def.id, 'idle'));
    this.sprite.setDisplaySize(CAT_DISPLAY_SIZE, CAT_DISPLAY_SIZE);
    this.add(this.sprite);

    this.startIdleAnimation();
  }

  private startIdleAnimation(): void {
    this.idleTween = this.scene.tweens.add({
      targets: this,
      scaleY: { from: 1, to: 1.035 },
      scaleX: { from: 1, to: 0.985 },
      duration: 620,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  getLane(): Lane {
    return this.lane;
  }

  /** Smoothly approach a normalized (0..1) target X within this cat's lane. */
  update(targetNormX: number, deltaSeconds: number): void {
    const prevX = this.x;
    this.currentNormX = smoothApproach(this.currentNormX, targetNormX, CAT_MOVE_SMOOTHING, deltaSeconds);
    this.x = this.laneOffsetPx + this.currentNormX * LANE_WIDTH;
    const velocity = deltaSeconds > 0 ? (this.x - prevX) / deltaSeconds : 0;
    const lean = Phaser.Math.Clamp(velocity / 900, -0.18, 0.18);
    this.rotation = Phaser.Math.Linear(this.rotation, lean, 0.25);
  }

  /** World-space catch hitbox used for overlap testing against falling food. */
  getCatchZone(): { x: number; y: number; halfWidth: number; y0: number; y1: number } {
    const halfWidthPx = CAT_CATCH_HALF_WIDTH * LANE_WIDTH;
    const mouthY = this.y + CAT_MOUTH_OFFSET_Y;
    return { x: this.x, y: mouthY, halfWidth: halfWidthPx, y0: mouthY - 34, y1: mouthY + 34 };
  }

  private setPose(pose: CatPose): void {
    this.sprite.setTexture(catTextureKey(this.catId, pose));
  }

  private revertToIdleAfter(delayMs: number): void {
    this.revertPoseTimer?.remove();
    this.revertPoseTimer = this.scene.time.delayedCall(delayMs, () => this.setPose('idle'));
  }

  /** Played once a falling treat enters the judgable timing window for this lane --
   * mouth open, looking up, ready to catch -- so the cat visibly anticipates the
   * catch rather than only reacting after it lands. Holds until overridden by
   * playEat() (caught) or playCry() (missed); every anticipated note resolves to
   * one or the other, so there's nothing to revert to idle from here. */
  playAnticipate(): void {
    this.setPose('anticipate');
    this.scene.tweens.add({
      targets: this,
      scaleX: { from: 1.06, to: 1 },
      scaleY: { from: 0.96, to: 1 },
      duration: 140,
      ease: 'Quad.easeOut',
    });
  }

  playEat(): void {
    this.setPose('eat');
    this.revertToIdleAfter(320);

    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scaleX: { from: 1.18, to: 1 },
      scaleY: { from: 0.82, to: 1 },
      duration: 260,
      ease: 'Elastic.easeOut',
      easeParams: [1, 0.6],
    });
  }

  /** Played when this cat's lane misses a treat, which ends the level -- no
   * revert-to-idle timer here since the scene is either about to transition to
   * Results, or (if the player pays to continue) explicitly cleared via
   * resetToIdle(). There's no AI-generated "crying" pose: this checkpoint's strong
   * bias toward happy expressions made a convincingly sad face unreliable across the
   * cat roster even with heavy prompt weighting (see ARCHITECTURE.md), so the cry
   * reads through a tint/droop treatment plus a couple of procedural falling tears
   * instead. */
  playCry(): void {
    this.scene.tweens.killTweensOf(this);
    this.sprite.setTint(MISS_TINT);
    this.scene.tweens.add({
      targets: this,
      angle: { from: -10, to: 0 },
      y: { from: this.y + 10, to: this.y },
      duration: 380,
      ease: 'Sine.easeOut',
    });
    this.spawnTears();
  }

  private spawnTears(): void {
    for (const dx of [-28, 28]) {
      const tear = this.scene.add.ellipse(dx, -22, 10, 14, 0x6fc3ff, 0.9);
      tear.setStrokeStyle(1, 0xffffff, 0.6);
      this.add(tear);
      this.scene.tweens.add({
        targets: tear,
        y: 55,
        scaleY: { from: 1, to: 1.4 },
        alpha: { from: 0.9, to: 0 },
        duration: 700 + Math.random() * 200,
        delay: Math.random() * 150,
        ease: 'Cubic.easeIn',
        onComplete: () => tear.destroy(),
      });
    }
  }

  /** Clears the level-fail visual (tint, droop, any still-falling tears) and returns
   * to idle -- used when a paid Continue resumes gameplay after a fail, undoing
   * playCry()'s effects since that method has no auto-revert of its own. */
  resetToIdle(): void {
    this.scene.tweens.killTweensOf(this);
    this.sprite.clearTint();
    this.setPose('idle');
    this.angle = 0;
    this.setScale(1);
  }

  playComboCelebration(): void {
    this.setPose('eat');
    this.revertToIdleAfter(420);

    if (!this.sparkle) {
      this.sparkle = this.scene.add.star(0, -70, 5, 6, 14, 0xffe066, 0.95);
      this.add(this.sparkle);
    }
    this.sparkle.setScale(0).setAlpha(1);
    this.scene.tweens.add({
      targets: this.sparkle,
      scale: { from: 0, to: 1.3 },
      alpha: { from: 1, to: 0 },
      rotation: { from: 0, to: 1.2 },
      duration: 500,
      ease: 'Cubic.easeOut',
    });
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scaleX: { from: 1.25, to: 1 },
      scaleY: { from: 1.25, to: 1 },
      duration: 380,
      ease: 'Elastic.easeOut',
      easeParams: [1, 0.5],
    });
  }

  destroy(fromScene?: boolean): void {
    this.idleTween?.stop();
    this.revertPoseTimer?.remove();
    super.destroy(fromScene);
  }
}
